<script lang="ts">
import type { PaymentMethod } from '@/types'
import { Icons } from '@/utils/helper-objects'
import { TextButton } from '@goodlifeplus/component-library'
import { type PropType, type SlotsType, defineComponent } from 'vue'

export default defineComponent({
  name: 'PaymentMethodsPanel',
  components: { TextButton },
  props: {
    showTitle: {
      type: Boolean,
      default: true,
    },
    paymentErrors: {
      type: String,
      default: null,
    },
    isPaymentReady: {
      type: Boolean,
      default: false,
    },
    isPaymentProcessing: {
      type: Boolean,
      default: false,
    },
    isAddNewCardOpen: {
      type: Boolean,
      default: false,
    },
    isOneClickPayOpen: {
      type: Boolean,
      default: false,
    },
    // Summary rows shown when one-click is active — label + amount in currency units (not pence)
    summaryItems: {
      type: Array as PropType<Array<{ label: string; amount: number }>>,
      default: () => [],
    },
    oneClickPay: {
      type: Object as PropType<PaymentMethod | null>,
      default: null,
    },
  },
  emits: [
    'update:isAddNewCardOpen',
    'update:isOneClickPayOpen',
    'clearPaymentErrors',
  ],
  slots: Object as SlotsType<{
    apmButtons?: {}
    paymentForm: { showForm: boolean }
  }>,
  data() {
    return {
      Icons,
    }
  },
  methods: {
    toggleAddNewCard() {
      this.$emit('update:isAddNewCardOpen', !this.isAddNewCardOpen)
    },
    toggleOneClickPay() {
      this.$emit('update:isOneClickPayOpen', !this.isOneClickPayOpen)
    },
  },
})
</script>

<template>
  <div class="flex flex-col gap-4 mr-2">
    <p v-if="showTitle" class="font-bold">
      <template v-if="isAddNewCardOpen">Add New Card</template>
      <template v-else-if="isOneClickPayOpen">1 Click Payment</template>
      <template v-else>Choose Payment Method</template>
    </p>

    <!-- Payment errors -->
    <div
      v-if="paymentErrors"
      class="mb-4 rounded-lg border-l-4 border-red-500 bg-red-50 p-4"
    >
      <div class="flex items-start justify-between">
        <div class="flex items-start">
          <AppIcon
            :icon="Icons.Error"
            class="mr-3 mt-1 text-red-500"
            size="sm"
          />
          <div>
            <h3 class="text-sm font-medium text-red-800">Payment Error</h3>
            <p class="mt-1 text-sm text-red-700" v-html="paymentErrors" />
          </div>
        </div>

        <button
          class="ml-4 inline-flex rounded-md bg-red-50 p-1.5 text-red-400 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
          @click="$emit('clearPaymentErrors')"
        >
          <AppIcon :icon="Icons.Times" size="xs" />
        </button>
      </div>
    </div>

    <!-- APM buttons (Apple Pay / Google Pay) — only rendered when parent fills the slot -->
    <div
      v-if="$slots.apmButtons"
      v-show="!isOneClickPayOpen && !isAddNewCardOpen"
      class="flex flex-col gap-4"
    >
      <slot name="apmButtons" />
      <div class="line-through-sides uppercase text-xs my-2">OR</div>
    </div>

    <!-- Summary shown when one-click is active -->
    <div
      v-if="summaryItems.length > 0 && isOneClickPayOpen"
      class="flex border-b flex-col border-b-slate-200"
    >
      <table class="w-full">
        <tbody>
          <tr v-for="item in summaryItems" :key="item.label">
            <td class="pb-3 pr-3 text-left">{{ item.label }}:</td>
            <td class="pb-3 text-right font-bold">
              {{ $n(item.amount, 'currency') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Card payment button -->
    <AppButton
      v-if="!isOneClickPayOpen && !isAddNewCardOpen"
      title="Pay With Card"
      class="button-secondary min-w-full text-base px-6 !font-bold h-10 !py-1 border m-[1px] border-slate-300"
      :disabled="!isPaymentReady || isPaymentProcessing"
      data-cy="add-new-card"
      @click="toggleAddNewCard"
    >
      <AppIcon :icon="Icons.Card" />
      Add New Card
      <AppIcon :icon="Icons.Visa" size="md" class="h-auto" />
      <AppIcon :icon="Icons.Mastercard" size="md" class="h-auto" />
    </AppButton>

    <TextButton
      v-if="oneClickPay && !isAddNewCardOpen && !isOneClickPayOpen"
      :variant="UI.TextButton.Variant.Primary"
      :size="UI.TextButton.Size.Large"
      :label="`1 Click Pay | •••• ${oneClickPay.last_4_digits}`"
      :disabled="!isPaymentReady || isPaymentProcessing"
      class="min-w-full"
      @click="toggleOneClickPay"
    />

    <!-- Payment form slot — exposes showForm so the parent doesn't need to recompute it -->
    <slot
      name="paymentForm"
      :show-form="
        (isAddNewCardOpen || isOneClickPayOpen) &&
        isPaymentReady &&
        !isPaymentProcessing
      "
    />
  </div>
</template>

<style scoped>
.payment-stage-solidgate {
  :deep(.payment-container) {
    @apply border border-slate-300 bg-primary-lighter p-3 rounded-lg;
  }
}
</style>
